/** @jsx jsx */
import { jsx } from '@emotion/core';
import { useState } from 'react';
import { Button, Form, Card } from 'react-bootstrap';
import Select from 'react-select';
import { useEffect } from 'react';
import axios from 'axios';
import { set, format, addDays, eachDayOfInterval, parse, startOfWeek, endOfDay } from 'date-fns';
import ja from 'date-fns/locale/ja';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faChevronLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import './reserve_trial_lesson.scss';

const TimeslotCellContents = ({ isAvailable, setStartTime }: any) => {
  const [faIcon, setFaIcon] = useState(faTimes);
  const [clickHandler, setClickHandler] = useState(() => {
    return () => {};
  });

  useEffect(() => {
    if (isAvailable) {
      setFaIcon(faCircle);
      setClickHandler(() => {
        return setStartTime;
      });
    } else {
      setFaIcon(faTimes);
      setClickHandler(() => {
        return () => {};
      });
    }
  }, [isAvailable]);

  return (
    <div onClick={clickHandler}>
      <FontAwesomeIcon icon={faIcon} />
    </div>
  );
};

export const ReserveTrialLesson = ({ grades, postUrl }: any) => {
  const [gradeId, setGradeId] = useState(1);
  const [startTime, setStartTime] = useState('');
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [fromDate, setFromDate] = useState(
    set(addDays(startOfWeek(new Date(), { weekStartsOn: 5 }), 3), {
      hours: 0,
      minutes: 0,
      seconds: 0,
    }),
  );
  const [toDate, setToDate] = useState(
    set(addDays(startOfWeek(new Date(), { weekStartsOn: 5 }), 7), {
      hours: 0,
      minutes: 0,
      seconds: 0,
    }),
  );
  const [loading, setLoading] = useState(false);

  const csrfToken = (document.querySelector('[name=csrf-token]') as any)?.content;
  const lessonSegments = [
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
  ];

  const loadSubscribableStartTimes = async () => {
    setLoading(true);
    await axios
      .get('/student/api/trialable_start_times', {
        params: {
          grade_id: gradeId,
          from: format(fromDate, 'yyyy-MM-dd HH:mm:ss'),
          to: format(endOfDay(toDate), 'yyyy-MM-dd HH:mm:ss'),
        },
      })
      .then((response) => {
        setAvailableStartTimes(
          response.data['trialable_start_times'].map((time: any) =>
            format(parse(time, "yyyy-MM-dd'T'HH:mm:ss.SSSxxx", new Date()), 'yyyy-MM-dd HH:mm:ss'),
          ),
        );
        setLoading(false);
      })
      .catch((_error) => {
        alert('読み込みに失敗しました。');
        setLoading(false);
      });
  };

  const ableToShowPrevWeek = () => {
    return toDate > addDays(new Date(), 7);
  };

  const prevWeek = () => {
    setFromDate(addDays(fromDate, -7));
    setToDate(addDays(toDate, -7));
  };

  const nextWeek = () => {
    setFromDate(addDays(fromDate, 7));
    setToDate(addDays(toDate, 7));
  };

  const isFormValid = () => {
    return startTime && gradeId;
  };

  useEffect(() => {
    loadSubscribableStartTimes();
  }, [toDate, gradeId]);

  useEffect(() => {
    setStartTime('');
  }, [gradeId]);

  return (
    <div>
      {loading && (
        <div
          style={{
            backgroundColor: 'white',
            zIndex: 10,
            opacity: 0.5,
            height: '100vh',
            fontSize: '60px',
          }}>
          Loading...
        </div>
      )}
      <Form action={postUrl} method="post">
        <input type="hidden" name="participation[start_time]" value={startTime} />
        <input type="hidden" name="free" value={1} />
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <Form.Group controlId="input">
          <Form.Label className={'label'}>１．ご希望のクラスを選択してください</Form.Label>
          <Select
            name="participation[grade_id]"
            options={grades}
            defaultValue={grades[0]}
            getOptionLabel={({ name }: any) => name}
            getOptionValue={({ id }: any) => id}
            onChange={(selectedOption: any) => {
              setGradeId(selectedOption.id);
            }}
          />
          <Card className="mt-2">
            <Card.Body>
              <p>※クラスのレベル目安・英語割合</p>
              <h6>＜Starter Class＞英語5割 日本語5割</h6>
              <p>
                楽しく英語に慣れ親しみ、コミュニケーションの大切さを身につけます。身近な英単語や日常会話を理解し、簡単に答えることができるようになります。
              </p>
              <h6>＜Middle Class＞英語7割 日本語3割</h6>
              <p>
                身近なトピックについて理解し、自分の気持ちなどを伝えるための基礎力を身につけます。好きなこと、できること、やりたいことについて相手とコミュニケーションがとれるようになります。
              </p>
              <h6>＜High Class＞英語9割　日本語1割</h6>
              <p>
                日常の生活や身の回りのトピックについて理解し、言いたいことを伝える表現力を身につけます。
                <br />
                状況を把握した上で自分の意見を述べ、相手のコミュニケーションがとれるようになります。
              </p>
              <h6>＜Upper Class＞英語10割　日本語0割</h6>
              <p>
                積み上げてきた英語力を応用し、日常生活に関するトピックについて実戦的な表現力を身につけます。
                <br />
                まとまった会話を聞いて、その中の情報を理解し、自分の意見とともに対話ができるようになります。
              </p>
            </Card.Body>
          </Card>
        </Form.Group>
        <Form.Group controlId="input">
          <div>
            <Form.Label className={'label'}>２．ご希望の日時を選択してください</Form.Label>
            {isFormValid() && (
              <div className={'submitButtonContainer'}>
                <p className="text-center font-weight-bold">
                  {startTime &&
                    format(
                      parse(startTime, 'yyyy-MM-dd HH:mm:ss', new Date()),
                      'yyyy年M月d日(EEEEE) HH:mm〜',
                      { locale: ja },
                    )}
                </p>
                {/* @ts-ignore Occurs during typescript migration */}
                <Button type="submit" variant="brand-purple">
                  予約する
                </Button>
              </div>
            )}
          </div>
          <div className="pb-2 d-flex justify-content-between">
            <Button
              disabled={!ableToShowPrevWeek()}
              onClick={prevWeek}
              size="sm"
              variant="outline-secondary"
              className="">
              <FontAwesomeIcon icon={faChevronLeft} /> 先週
            </Button>
            <div className="d-flex align-items-center font-weight-bold month">
              {format(fromDate, 'M月', { locale: ja })}
            </div>
            <Button onClick={nextWeek} size="sm" variant="outline-secondary" className="">
              次週 <FontAwesomeIcon icon={faChevronRight} />
            </Button>
          </div>

          <table className="table table-bordered text-center">
            <thead>
              <tr>
                <th></th>
                {eachDayOfInterval({ start: fromDate, end: toDate }).map((date) => (
                  <th key={'day-' + format(date, 'yyyy-MM-dd')}>
                    {format(date, 'd日(EEEEE)', { locale: ja })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lessonSegments.map((lessonSegment) => (
                <tr key={'time-' + lessonSegment}>
                  <td>{lessonSegment}</td>
                  {eachDayOfInterval({ start: fromDate, end: toDate }).map((date) => {
                    const yyyy_mm_dd_hh_mm_ss =
                      format(date, 'yyyy-MM-dd') + ' ' + lessonSegment + ':00';
                    return (
                      <td
                        key={'datetime-' + yyyy_mm_dd_hh_mm_ss}
                        className={`${startTime == yyyy_mm_dd_hh_mm_ss ? 'selected' : ''} ${
                          _.includes(availableStartTimes, yyyy_mm_dd_hh_mm_ss) ? 'available' : ''
                        }`}>
                        <TimeslotCellContents
                          isAvailable={_.includes(availableStartTimes, yyyy_mm_dd_hh_mm_ss)}
                          setStartTime={() => {
                            setStartTime(yyyy_mm_dd_hh_mm_ss);
                          }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Form.Group>
      </Form>
    </div>
  );
};
